import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PartFindingModel } from 'src/app/api/proxy/practice/models/part-finding-model';
import { BodyPart } from 'src/app/controls/anatomy/anatomy.component';
import { ITreatment } from 'src/app/services/ble/ITreatment';
import { TranslatorService } from 'src/app/services/translator-service/translator.service';
import { IPartItem } from 'src/app/services/protocol-service/IProtocol';
import { PromptService } from 'src/app/services/promtp-service/prompt.service';
import { IonicSafeString, ModalController } from '@ionic/angular';
import { IPatient } from 'src/app/services/person-service/IPerson';
import { DatePipe } from '@angular/common';
import { firstValueFrom, Subscription } from 'rxjs';
import { PracticePatientService } from 'src/app/api/proxy/practice/services/practice-patient.service';
import { PractitionerService } from 'src/app/services/practitioner-service/practitioner.service';
import { TreatmentService } from 'src/app/services/treatment-service/treatment.service';
import { getPatientFullName } from 'src/app/utilities/patient.utility';
import {
  EncounterDetailModel,
  FacilityModel,
  FacilityStatusType,
  PatientEncounterModel,
  PatientModel,
} from 'src/app/api/proxy/practice/practice-models';
import { Events } from 'src/app/services/events/events';
import { IdleTimeoutService } from 'src/app/services/idle-timeout/idle-timeout-service';
import { TreatmentAdjustComponent, TreatmentAdjustUiOptions } from './adjust/adjust.page';
import { TreatmentReviewComponent } from './review/review.page';
import {
  HeaderComponent,
  MenuActionButton,
  MenuPage,
} from 'src/app/components/header/header.component';
import { TreatmentProfileComponent } from './profile/profile.component';
import { PermissionDefinitions } from 'src/app/services/authorization/permission-definitions';
import { PermissionAccessService } from 'src/app/services/authorization/permission-access.service';
import { StoragePrefixType } from 'src/app/services/secure-storage/secure-storage-service';
import { DeferredApplyService } from 'src/app/services/deferred-apply-service/deferred-apply.service';
import { NetworkService } from 'src/app/services/network-service/network-service';
import { EnvironmentConfigService } from 'src/app/services/environment-config-service/environment-config-service';
import { SessionService } from 'src/app/services/session-service/session-service';
import { UserInfoService } from 'src/app/services/auth-service/user-info-service';
import { EncounterRegionNoteModel } from 'src/app/api/proxy/practice/models/encounter-region-note-model';
import { PatientTreatmentQueueModel } from 'src/app/api/proxy/practice/models/patient-treatment-queue-model';
import { PatientTreatmentQueuePartItemModel } from 'src/app/api/proxy/practice/models/patient-treatment-queue-part-item-model';
import { PatientTreatmentQueueTreatmentNoteModel } from 'src/app/api/proxy/practice/models/patient-treatment-queue-treatment-note-model';
import { NotesModel } from 'src/app/api/proxy/practice/models/notes-model';
import { resolveRegionNotesForDuplicate } from 'src/app/services/treatment-service/pending-soap.util';
import { TreatmentQueueStatusType } from 'src/app/api/proxy/practice/models/treatment-queue-status-type';
import { PracticePatientTreatmentQueueService } from 'src/app/api/proxy/practice/services/practice-patient-treatment-queue.service';
import {
  countQueuedSessionAdjustments,
  hasUntreatedAssignedParts,
  incompleteVisitQueueTarget,
  queuedSessionHadTreatments,
  queuedTreatmentResumeEncounterId,
  shouldCompleteQueuedTreatment,
  shouldCompleteSavedQueuedTreatmentOnClose,
  shouldLinkQueuedTreatmentEncounter,
  treatedEncounterPartKeys,
  untreatedQueuedFindingKeys,
} from 'src/app/shared/queued-treatment-close.flow';
import { startQueuedSession } from 'src/app/shared/queued-treatment-open.flow';
import { buildPatientTreatmentQueuePostBody } from 'src/app/shared/patient-treatment-queue-post.builder';

export type EncounterOpenMode =
  | 'review'
  | 'recall'
  | 'edit'
  | 'newTreatment'
  | 'continueCurrent'
  | 'openQueued';

export interface EncounterOpenEvent {
  mode: EncounterOpenMode;
  encounterId?: string | null;
  queuedTreatmentId?: string | null;
}

@Component({
  selector: 'app-treatment',
  templateUrl: './treatment.page.html',
  styleUrls: ['./treatment.page.scss'],
  standalone: false,
  providers: [DatePipe],
})
export class TreatmentPage implements OnInit, OnDestroy {
  private _adjustmentComponent?: TreatmentAdjustComponent;
  @ViewChild('adjustmentComponent')
  set adjustmentComponent(component: TreatmentAdjustComponent | undefined) {
    this._adjustmentComponent = component;
    if (component) {
      this.deferredApplyService.register<TreatmentAdjustUiOptions>(
        'treatment-adjust',
        component,
        (ref, payload) => {
          (ref as TreatmentAdjustComponent).applyOptions(payload);
        },
      );
    } else {
      // Avoid applying a later queue payload to a destroyed Adjust instance.
      this.deferredApplyService.clear('treatment-adjust');
    }
  }
  get adjustmentComponent(): TreatmentAdjustComponent | undefined {
    return this._adjustmentComponent;
  }
  @ViewChild('reviewComponent') reviewComponent?: TreatmentReviewComponent;
  @ViewChild('appHeaderComponent') appHeaderComponent?: HeaderComponent;
  @ViewChild('profileComponent') profileComponent?: TreatmentProfileComponent;

  subscriptions: Subscription[] = [];
  headerTitle = '';
  patient: IPatient | null = null;
  patientModel!: PatientModel;
  selectedFacility!: FacilityModel | null;
  patientId = '';
  navLine1 = '';
  navLine2 = '';

  pageView: 'profile' | 'adjust' | 'review' | 'result' = 'profile';
  isReviewEnabled = false;
  visitNumber = '';
  dateText = '';
  paginationText = '';
  recallEnabled = false;
  enableCompleteEncounter = false;
  showCurrentVisitBadge = false;
  /** Left arrow: hide when viewing the oldest visit. */
  navDisabledPrev = false;
  /** Right arrow: hide when viewing the newest visit. */
  navDisabledNext = false;
  defaultPlaylist: ITreatmentPlayListItem[] = [];

  menuPages: MenuPage[];
  actionButtons: MenuActionButton[] = [];

  hasPatientWritePermission: boolean = false;
  hasTreatmentViewPermission: boolean = true;
  hasTreatmentProfileTreat = false;
  hasTreatmentProfileReview = false;
  hasTreatmentProfileResultsView = false;
  hasTreatmentProfileDuplicate = false;
  hasTreatmentProfileRecall = false;
  hasTreatmentProfileEdit = false;
  hasOnlineAdjustment = false;
  private currentPractitionerId: string | null = null;
  private reviewEncounter: EncounterDetailModel | null = null;
  private metadataEditSession = false;
  /** True after Save/Discard/clean leave so Close does not persist edit-session drafts again. */
  private metadataEditCloseResolved = false;
  /** Stays true after leaving Edit so Profile/Adjust cannot re-promote that visit as CURRENT. */
  private suppressWorkingCurrentAfterMetadataEdit = false;
  private hasTreatmentProfileResultsViewResolved = false;

  /** Set when close clears the patient session so ionViewWillLeave cannot re-persist Adjust draft. */
  private patientSessionClosed = false;

  /** Blocks tab spam while a Load* transition is in progress. */
  private isTabNavigationLocked = false;

  /** Queue parts from Welcome when the user chose to load a queued treatment. */
  queuedTreatmentParts: PatientTreatmentQueuePartItemModel[] | null = null;
  queuedTreatmentNotes: NotesModel | null = null;
  queuedTreatmentRegionNotes: EncounterRegionNoteModel[] | null = null;
  queuedTreatmentTreatmentNotes: PatientTreatmentQueueTreatmentNoteModel[] | null = null;
  private pendingQueuedTreatment: PatientTreatmentQueueModel | null = null;

  /** Queue id for the in-progress Adjust session, when Profile should hide that Open row. */
  get currentQueuedTreatmentId(): string | null {
    return this.pendingQueuedTreatment?.id ?? null;
  }
  private skipNextPatientSelectedProfileLoad = false;
  private queuedAdjustOpenApplied = false;
  private queuedAppStateListener?: PluginListenerHandle;
  private lastInProgressQueuePersistAt = 0;
  private static readonly IN_PROGRESS_QUEUE_PERSIST_MS = 15_000;
  private lastAdjustPlaylist: ITreatmentPlayListItem[] | null = null;
  private queuedSessionAdjustmentCountAtOpen = 0;

  private adjustPage!: MenuPage;
  private profilePage!: MenuPage;
  private reviewPage!: MenuPage;
  private resultPage!: MenuPage;

  constructor(
    protected _translatorService: TranslatorService,
    protected _router: Router,
    protected route: ActivatedRoute,
    protected activatedRoute: ActivatedRoute,
    protected _promptService: PromptService,
    protected modalCtrl: ModalController,
    protected _practicePatientService: PracticePatientService,
    protected _practitionerService: PractitionerService,
    protected treatmentService: TreatmentService,
    protected _datePipe: DatePipe,
    protected _networkService: NetworkService,
    private deferredApplyService: DeferredApplyService,
    private cdr: ChangeDetectorRef,
    private envService: EnvironmentConfigService,
    private readonly permissionAccess: PermissionAccessService,
    private sessionService: SessionService,
    private readonly queueService: PracticePatientTreatmentQueueService,
  ) {
    this.subscriptions.push(
      Events.patientSelected.subscribe(async (data: PatientModel) => {
        this.patientSessionClosed = false;
        if (this.skipNextPatientSelectedProfileLoad) {
          this.skipNextPatientSelectedProfileLoad = false;
          return;
        }
        await this.LoadProfileComponent();
      }),
    );

    this.subscriptions.push(
      IdleTimeoutService.onTimeout$.subscribe(async () => {
        try {
          // Persist encounter only — SessionService owns logout (no welcome redirect).
          await this.closeProfile(true, {
            skipIdleReset: true,
            skipSessionClear: true,
            skipNavigation: true,
          });
        } finally {
          this.sessionService.unregisterIdleTimeoutSubscriber('TreatmentPage');
        }
      }),
    );

    this.adjustPage = new MenuPage(
      this._translatorService.translateText('treatment-page.menu-pages.adjust'),
      async () => {
        await this.LoadAdjustComponent();
      },
    );
    this.profilePage = new MenuPage(
      this._translatorService.translateText('treatment-page.menu-pages.profile'),
      async () => {
        await this.LoadProfileComponent();
      },
    );
    this.reviewPage = new MenuPage(
      this._translatorService.translateText('treatment-page.menu-pages.review'),
      async () => {
        await this.LoadReviewComponent();
      },
    );
    this.resultPage = new MenuPage(
      this._translatorService.translateText('treatment-page.menu-pages.result'),
      async () => {
        await this.LoadResultComponent();
      },
    );

    this.menuPages = [this.profilePage, this.reviewPage, this.resultPage];
  }

  async ngOnInit() {
    this.sessionService.registerIdleTimeoutSubscriber('TreatmentPage', 'timeout');
    void this.registerQueuedSessionBackgroundPersist();
    this.captureQueuedTreatmentFromNav();
    await this.treatmentService.loadSavedAdjustmentMode();
    this.patientSessionClosed = false;

    this.patientId = this.activatedRoute.snapshot.paramMap.get('patientId') as string;
    const [
      hasTreatmentViewPermission,
      hasTreatmentProfileTreat,
      hasTreatmentProfileReview,
      hasTreatmentProfileResultsView,
      hasTreatmentProfileDuplicate,
      hasTreatmentProfileRecall,
      hasTreatmentProfileEdit,
      hasOnlineAdjustment,
    ] = await Promise.all([
      this.permissionAccess.canView(PermissionDefinitions.PATIENT_LIST_VIEW),
      this.permissionAccess.canAct(PermissionDefinitions.TREATMENT_PROFILE_TREAT),
      this.permissionAccess.canView(PermissionDefinitions.TREATMENT_PROFILE_TREATMENT_REVIEW),
      this.permissionAccess.canView(PermissionDefinitions.TREATMENT_PROFILE_RESULTS_VIEW),
      this.permissionAccess.canAct(PermissionDefinitions.TREATMENT_PROFILE_TREATMENT_DUPLICATE),
      this.permissionAccess.canAct(PermissionDefinitions.TREATMENT_PROFILE_TREATMENT_RECALL),
      this.permissionAccess.canAct(PermissionDefinitions.TREATMENT_PROFILE_TREATMENT_EDIT),
      this.permissionAccess.canAct(PermissionDefinitions.TREATMENT_ADJUST_ONLINE_ADJUST),
    ]);

    this.hasTreatmentViewPermission = hasTreatmentViewPermission;
    this.hasTreatmentProfileTreat = hasTreatmentProfileTreat;
    this.hasTreatmentProfileReview = hasTreatmentProfileReview;
    this.hasTreatmentProfileResultsView = hasTreatmentProfileResultsView;
    this.hasTreatmentProfileDuplicate = hasTreatmentProfileDuplicate;
    this.hasTreatmentProfileRecall = hasTreatmentProfileRecall;
    this.hasTreatmentProfileEdit = hasTreatmentProfileEdit;
    this.hasOnlineAdjustment = hasOnlineAdjustment;
    this.currentPractitionerId = (await UserInfoService.getUserInfo())?.practitionerId ?? null;
    this.hasTreatmentProfileResultsViewResolved = true;
    if (this.patientId) {
      this.selectedFacility = await this._practitionerService.getSelectedFacility();
      try {
        const patient = await firstValueFrom(
          this._networkService.performNetworkCacheFallbackCall(
            StoragePrefixType.Patient,
            `${this.selectedFacility!.id!}-${this.patientId}`,
            this._practicePatientService.patientIdGet({
              id: this.patientId,
            }),
          ),
        );

        if (patient) {
          this.patientModel = patient as PatientModel;
          this.headerTitle = getPatientFullName(this.patientModel);
        }

        await this.updatePermissions();
        await this.treatmentService.setActivePatient(this.patientId);
        this.captureQueuedTreatmentFromNav();
        if (!(await this.openQueuedAdjustIfPending())) {
          this.LoadProfileComponent();
        }
      } catch (e) {
        this._promptService.showNetworkConnectionError(e, 'Error fetching patient data');
      }
    }
  }

  /** Updates permissions and adjust tab menu state. */
  private async updatePermissions(): Promise<void> {
    const permissionChecks: Array<Promise<boolean>> = [
      this.permissionAccess.canAct(PermissionDefinitions.TREATMENT_PROFILE_TREAT),
      this.permissionAccess.canView(PermissionDefinitions.TREATMENT_PROFILE_TREATMENT_REVIEW),
      this.permissionAccess.canAct(PermissionDefinitions.TREATMENT_PROFILE_TREATMENT_DUPLICATE),
      this.permissionAccess.canAct(PermissionDefinitions.TREATMENT_PROFILE_TREATMENT_RECALL),
      this.permissionAccess.canAct(PermissionDefinitions.TREATMENT_PROFILE_TREATMENT_EDIT),
      this.permissionAccess.canAct(PermissionDefinitions.PATIENT_LIST_WRITE),
      this.permissionAccess.canAct(PermissionDefinitions.TREATMENT_ADJUST_ONLINE_ADJUST),
    ];

    if (!this.hasTreatmentProfileResultsViewResolved) {
      permissionChecks.push(
        this.permissionAccess.canView(PermissionDefinitions.TREATMENT_PROFILE_RESULTS_VIEW),
      );
    }

    const results = await Promise.all(permissionChecks);
    this.hasTreatmentProfileTreat = results[0];
    this.hasTreatmentProfileReview = results[1];
    this.hasTreatmentProfileDuplicate = results[2];
    this.hasTreatmentProfileRecall = results[3];
    this.hasTreatmentProfileEdit = results[4];
    this.hasPatientWritePermission = results[5];
    this.hasOnlineAdjustment = results[6];
    this.currentPractitionerId = (await UserInfoService.getUserInfo())?.practitionerId ?? null;
    if (!this.hasTreatmentProfileResultsViewResolved) {
      this.hasTreatmentProfileResultsView = results[7];
      this.hasTreatmentProfileResultsViewResolved = true;
    }

    this.applyMenuPageDisabledState();
    this.rebuildMenuPages();
    this.buildReviewActionButtons();
  }

  /** BLE connect/mode chrome — hidden during metadata edit so Review stays the treatment context. */
  get showAdjustDeviceControls(): boolean {
    if (this.metadataEditSession || !this.hasTreatmentProfileTreat) {
      return false;
    }
    const platform = this.envService.Platform;
    if (platform === 'android') {
      return true;
    }
    return platform === 'web' && this.hasOnlineAdjustment;
  }

  /** Web-only Connect/Mode header chrome when user has online adjustment permission. */
  get allowWebConnectButton(): boolean {
    return this.envService.Platform === 'web' && this.hasOnlineAdjustment;
  }

  /** Full Adjust UI (tabs, new/continue/queued, playlist). BLE chrome is gated separately. */
  get supportsFullAdjust(): boolean {
    return true;
  }

  /** When true, Adjust is findings/notes/side/vector only — no add/adjust treatments. */
  get isMetadataEditOnly(): boolean {
    return this.metadataEditSession;
  }

  private consumeQueuedTreatmentFromNav(): PatientTreatmentQueueModel | null {
    const navState = this._router.getCurrentNavigation()?.extras?.state as
      | { queuedTreatment?: PatientTreatmentQueueModel }
      | undefined;
    const historyState =
      typeof history !== 'undefined'
        ? (history.state as { queuedTreatment?: PatientTreatmentQueueModel } | undefined)
        : undefined;
    const queued = navState?.queuedTreatment ?? historyState?.queuedTreatment ?? null;
    if (queued && historyState?.queuedTreatment) {
      const nextState = { ...historyState };
      delete nextState.queuedTreatment;
      history.replaceState(nextState, '');
    }
    return queued ?? null;
  }

  /** Capture nav-state queue early so patientSelected does not open Profile first. */
  private captureQueuedTreatmentFromNav(): void {
    const queued = this.consumeQueuedTreatmentFromNav() ?? this.pendingQueuedTreatment;
    if (!queued) {
      return;
    }
    this.pendingQueuedTreatment = queued;
    this.skipNextPatientSelectedProfileLoad = true;
    this.queuedTreatmentParts = queued.parts ?? [];
    this.queuedTreatmentNotes = queued.notes ?? null;
    this.queuedTreatmentRegionNotes = queued.regionNotes ?? null;
    this.queuedTreatmentTreatmentNotes = queued.treatmentNotes ?? null;
  }

  private async openQueuedAdjustIfPending(): Promise<boolean> {
    const queuedTreatment = this.pendingQueuedTreatment;
    if (!queuedTreatment || !this.supportsFullAdjust || !this.canAccessAdjustView()) {
      return false;
    }
    if (!this.patientModel) {
      return false;
    }
    if (this.queuedAdjustOpenApplied && this.pageView === 'adjust') {
      return true;
    }
    this.skipNextPatientSelectedProfileLoad = true;
    this.queuedTreatmentParts = queuedTreatment.parts ?? [];
    this.queuedTreatmentNotes = queuedTreatment.notes ?? null;
    this.queuedTreatmentRegionNotes = queuedTreatment.regionNotes ?? null;
    this.queuedTreatmentTreatmentNotes = queuedTreatment.treatmentNotes ?? null;
    const queueId = queuedTreatment.id ?? null;
    if (this.patientId && queueId) {
      await this.treatmentService.setCachedQueuedTreatmentId(this.patientId, queueId);
    }

    const resumeEncounterId = queuedTreatmentResumeEncounterId(queuedTreatment);
    if (resumeEncounterId) {
      await this.LoadAdjustComponent(false, resumeEncounterId);
    } else if (
      this.patientId &&
      queueId &&
      (await this.treatmentService.shouldResumeUnlinkedQueuedDraft(this.patientId, queueId))
    ) {
      await this.LoadAdjustComponent(false, null, { skipQueuedPartsHydration: true });
    } else {
      await this.LoadAdjustComponent(false, null, { skipEncounterResume: true });
    }
    this.queuedAdjustOpenApplied = true;
    this.captureQueuedSessionBaseline();
    return true;
  }

  private async linkQueuedTreatmentEncounter(
    queuedTreatment: PatientTreatmentQueueModel,
    encounterId: string,
  ): Promise<void> {
    if (!queuedTreatment.id || queuedTreatment.encounterId === encounterId) {
      return;
    }

    try {
      await firstValueFrom(
        this.queueService.patientTreatmentQueueIdUpdateEncounterEncounterIdPost({
          id: queuedTreatment.id,
          encounterId,
        }),
      );
      queuedTreatment.encounterId = encounterId;
    } catch (error) {
      await this._promptService.showNetworkConnectionError(
        error,
        'patient-treatment-queue-link-encounter',
      );
    }
  }

  private async completeQueuedTreatmentStatus(queueId: string): Promise<void> {
    try {
      await firstValueFrom(
        this.queueService.patientTreatmentQueueIdUpdateStatusStatusPost({
          id: queueId,
          status: TreatmentQueueStatusType.Completed,
        }),
      );
    } catch (error) {
      await this._promptService.showNetworkConnectionError(
        error,
        'patient-treatment-queue-complete',
      );
    }
  }

  private captureQueuedSessionBaseline(): void {
    const fromEncounter = this.adjustmentComponent?.encounterDetailModel?.treatments;
    const fromPlaylist = this.adjustmentComponent?.playlist?.flatMap((row) => row.treatments ?? []);
    this.queuedSessionAdjustmentCountAtOpen = countQueuedSessionAdjustments(
      fromEncounter?.length ? fromEncounter : fromPlaylist,
    );
  }

  private clearQueuedTreatmentOpenState(): void {
    this.pendingQueuedTreatment = null;
    this.queuedTreatmentParts = null;
    this.queuedTreatmentNotes = null;
    this.queuedTreatmentRegionNotes = null;
    this.queuedTreatmentTreatmentNotes = null;
    this.skipNextPatientSelectedProfileLoad = false;
    this.queuedAdjustOpenApplied = false;
    this.queuedSessionAdjustmentCountAtOpen = 0;
  }

  private clearQueuedPartsSnapshotForAdjustResume(): void {
    this.queuedTreatmentParts = null;
    this.queuedTreatmentRegionNotes = null;
    this.queuedTreatmentTreatmentNotes = null;
  }

  private async resolveContinueCurrentRecallId(
    encounterId?: string | null,
  ): Promise<string | null> {
    if (encounterId) {
      return encounterId;
    }
    const fromQueue = queuedTreatmentResumeEncounterId(this.pendingQueuedTreatment);
    if (fromQueue) {
      return fromQueue;
    }
    if (!this.patientId) {
      return null;
    }
    const current = await this.treatmentService.getCurrentEncounter(this.patientId, {
      allowWhenUninitialized: true,
    });
    return current?.id ?? null;
  }

  /** Disables all tabs while switching; Adjust stays permission-gated when unlocked. */
  private applyMenuPageDisabledState(): void {
    const locked = this.isTabNavigationLocked;
    this.profilePage.isDisabled = locked;
    this.reviewPage.isDisabled = locked;
    this.resultPage.isDisabled = locked;
    this.adjustPage.isDisabled =
      locked || !this.canAccessAdjustView() || this.metadataEditSession;
  }

  private beginTabNavigation(): boolean {
    if (this.isTabNavigationLocked) {
      return false;
    }
    this.isTabNavigationLocked = true;
    this.applyMenuPageDisabledState();
    this.cdr.detectChanges();
    return true;
  }

  private endTabNavigation(): void {
    this.isTabNavigationLocked = false;
    this.applyMenuPageDisabledState();
    this.cdr.detectChanges();
  }

  get editModeBanner(): string | null {
    if (!this.metadataEditSession) {
      return null;
    }
    return this._translatorService.translateText('treatment-page.buttons.edit-mode');
  }

  private buildAdjustActionButtons(): void {
    if (this.pageView !== 'adjust') {
      return;
    }

    this.actionButtons = [];
  }

  private buildReviewActionButtons(): void {
    if (this.pageView !== 'review') {
      return;
    }

    this.actionButtons = [];

    if (this.selectedFacility?.status !== FacilityStatusType.Active) {
      return;
    }

    // Current in-progress visit: no recall/duplicate — user should continue on Adjust.
    if (this.showCurrentVisitBadge) {
      return;
    }

    if (
      this.hasPatientWritePermission &&
      this.recallEnabled &&
      this.hasTreatmentProfileRecall
    ) {
      this.actionButtons.push(
        new MenuActionButton(
          this._translatorService.translateText('treatment-page.buttons.recall'),
          async () => {
            await this.recallTreatment();
          },
        ),
      );
    }

    if (this.canShowReviewEditButton()) {
      this.actionButtons.push(
        new MenuActionButton(
          this._translatorService.translateText('treatment-page.buttons.edit'),
          async () => {
            await this.editReviewedTreatment();
          },
        ),
      );
    }

    const onSupportedPlatform = this.supportsFullAdjust;
    if (onSupportedPlatform && this.hasTreatmentProfileDuplicate) {
      this.actionButtons.push(
        new MenuActionButton(
          this._translatorService.translateText('treatment-page.buttons.duplicate'),
          async () => {
            await this.duplicateTreatment();
          },
        ),
      );
    }
  }

  private canShowReviewEditButton(): boolean {
    if (!this.hasTreatmentProfileEdit || this.showCurrentVisitBadge) {
      return false;
    }

    const encounter =
      this.reviewEncounter ?? this.reviewComponent?.encounterDetailModel ?? null;
    if (!encounter) {
      return false;
    }

    return this.treatmentService.isEditableEncounter(
      encounter,
      this.currentPractitionerId,
      this.selectedFacility?.status === FacilityStatusType.Active,
      this.hasTreatmentProfileEdit,
    );
  }

  private async editReviewedTreatment(): Promise<void> {
    const encounterId =
      this.reviewEncounter?.id ?? this.reviewComponent?.encounterDetailModel?.id ?? null;
    if (!encounterId || !this.canShowReviewEditButton()) {
      return;
    }
    await this.OpenEncounter({ mode: 'edit', encounterId });
  }

  private shouldShowAdjustTab(): boolean {
    return (
      this.supportsFullAdjust &&
      this.canAccessAdjustView() &&
      this.selectedFacility?.status !== FacilityStatusType.Inactive
    );
  }

  private canAccessAdjustView(): boolean {
    return (
      this.hasTreatmentProfileTreat ||
      this.hasTreatmentProfileRecall ||
      this.hasTreatmentProfileEdit
    );
  }

  private rebuildMenuPages(): void {
    this.menuPages = [this.profilePage];
    if (this.shouldShowAdjustTab()) {
      this.menuPages.push(this.adjustPage);
    }
    if (this.hasTreatmentProfileReview) {
      this.menuPages.push(this.reviewPage);
    }
    if (this.hasTreatmentProfileResultsView) {
      this.menuPages.push(this.resultPage);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    void this.queuedAppStateListener?.remove();
    this.queuedAppStateListener = undefined;
    this.sessionService.unregisterIdleTimeoutSubscriber('TreatmentPage');
  }

  async ionViewWillEnter() {
    this.patientSessionClosed = false;
    this.captureQueuedTreatmentFromNav();
    await this.updatePermissions();
    if (this.patientModel) {
      await this.openQueuedAdjustIfPending();
    }
    this.cdr.detectChanges();
  }

  private async syncAdjustEncounterToCacheIfNeeded(): Promise<void> {
    if (this.metadataEditSession || this.suppressWorkingCurrentAfterMetadataEdit) {
      return;
    }
    if (this.pageView === 'adjust' && this.adjustmentComponent) {
      await this.adjustmentComponent.syncEncounterToCache();
      if (this.patientId && this.pendingQueuedTreatment?.id) {
        await this.treatmentService.setCachedQueuedTreatmentId(
          this.patientId,
          this.pendingQueuedTreatment.id,
        );
      }
      await this.linkPendingQueuedTreatmentToCurrentEncounter();
      await this.persistInProgressQueuedTreatmentIfNeeded();
    }
  }

  private async registerQueuedSessionBackgroundPersist(): Promise<void> {
    try {
      this.queuedAppStateListener = await App.addListener('appStateChange', async (state) => {
        if (state.isActive || this.patientSessionClosed) {
          return;
        }
        await this.syncAdjustEncounterToCacheIfNeeded();
        await this.persistInProgressQueuedTreatmentIfNeeded({ force: true });
      });
    } catch {
      // Web / non-Capacitor: no background hook.
    }
  }

  /** Best-effort server queue upsert while the queued visit is still in Adjust (InProgress). */
  private async persistInProgressQueuedTreatmentIfNeeded(options?: {
    force?: boolean;
  }): Promise<void> {
    const queuedTreatment = this.pendingQueuedTreatment;
    if (
      !queuedTreatment?.id ||
      this.pageView !== 'adjust' ||
      !this.adjustmentComponent?.isSetupComplete
    ) {
      return;
    }

    const playlist = this.adjustmentComponent.playlist;
    if (!playlist?.length) {
      return;
    }
    const hasWork =
      playlist.some((row) => (row.partId ?? 0) > 0) &&
      (playlist.some((row) => (row.treatments?.length ?? 0) > 0) ||
        hasUntreatedAssignedParts(playlist));
    if (!hasWork) {
      return;
    }

    if (
      !options?.force &&
      Date.now() - this.lastInProgressQueuePersistAt <
        TreatmentPage.IN_PROGRESS_QUEUE_PERSIST_MS
    ) {
      return;
    }

    const patientId = this.patientId;
    const facility =
      this.selectedFacility ?? (await this._practitionerService.getSelectedFacility());
    const clinicGroupId = facility?.clinicGroupId;
    if (!patientId || !clinicGroupId) {
      return;
    }

    const currentEncounter = await this.treatmentService.getCurrentEncounter(patientId, {
      allowWhenUninitialized: true,
    });
    const encounterId = currentEncounter?.id ?? null;

    try {
      await firstValueFrom(
        this.queueService.patientTreatmentQueuePatientTreatmentQueuePost({
          body: buildPatientTreatmentQueuePostBody({
            isCreateMode: false,
            queueId: queuedTreatment.id,
            clinicGroupId,
            patientId,
            loadedEncounterId: encounterId ?? queuedTreatment.encounterId ?? null,
            loadedStatus: TreatmentQueueStatusType.InProgress,
            playlist,
            notes:
              this.adjustmentComponent.encounterDetailModel?.notes ??
              this.treatmentService.getPendingSoapNotes(patientId),
            regionNotes: resolveRegionNotesForDuplicate({
              regionNotes: this.adjustmentComponent.encounterDetailModel?.regionNotes,
              pendingRegionPartDataByNoteRegion: this.treatmentService.getPendingRegionPartDataByNoteRegion(
                patientId,
              ),
              objectiveNotes:
                this.adjustmentComponent.encounterDetailModel?.notes?.objectiveNotes ?? null,
            }),
            statusOverride: TreatmentQueueStatusType.InProgress,
          }),
        }),
      );
      this.lastInProgressQueuePersistAt = Date.now();
    } catch (error) {
      await this._promptService.showNetworkConnectionError(
        error,
        'patient-treatment-queue-in-progress',
      );
      return;
    }

    if (
      encounterId &&
      shouldLinkQueuedTreatmentEncounter({
        queuedTreatmentId: queuedTreatment.id,
        encounterId,
      })
    ) {
      await this.linkQueuedTreatmentEncounter(queuedTreatment, encounterId);
    }
  }

  async ionViewWillLeave() {
    if (this.patientSessionClosed) {
      return;
    }
    await this.syncAdjustEncounterToCacheIfNeeded();
  }

  treatmentDataChanged(reviewData: {
    isReviewSelected: boolean;
    EncounterDetailModel: EncounterDetailModel | null | undefined;
    paginationText: string | null | undefined;
    dateText: string | null | undefined;
    isViewingCurrentTreatment?: boolean;
    /** False when on newest visit — hide right arrow. */
    canGoNewer?: boolean;
    /** False when on oldest visit — hide left arrow. */
    canGoOlder?: boolean;
  }) {
    this.paginationText = reviewData.paginationText || '';
    this.dateText = reviewData.dateText || '';
    this.visitNumber = reviewData.EncounterDetailModel?.visitNumber || '';
    this.reviewEncounter = reviewData.EncounterDetailModel ?? null;
    this.showCurrentVisitBadge =
      this.pageView === 'review' && !!reviewData.isViewingCurrentTreatment;
    if (this.pageView === 'review') {
      // Left = older, right = newer (see adjustNavPrevious / adjustNavNext).
      this.navDisabledPrev = reviewData.canGoOlder === false;
      this.navDisabledNext = reviewData.canGoNewer === false;
      this.buildReviewActionButtons();
    }
  }

  private async canLeaveProfile(): Promise<boolean> {
    if (this.pageView !== 'profile' || !this.profileComponent) {
      return true;
    }
    return this.profileComponent.confirmLeave();
  }

  private async canLeaveAdjustMetadataEdit(): Promise<boolean> {
    if (!this.metadataEditSession || this.pageView !== 'adjust') {
      return true;
    }

    const adjust = this.adjustmentComponent;
    if (!adjust?.hasMetadataEdits()) {
      this.metadataEditSession = false;
      this.metadataEditCloseResolved = true;
      this.suppressWorkingCurrentAfterMetadataEdit = true;
      if (this.patientId) {
        this.treatmentService.suppressResumeCurrentEncounter(this.patientId);
      }
      this.applyMenuPageDisabledState();
      return true;
    }

    const choice = await this._promptService.unsavedPatientProfileChoice(
      this._translatorService.translateText('treatment-page.messages.unsaved-edit-message'),
      this._translatorService.translateText('treatment-page.messages.unsaved-edit-save'),
      this._translatorService.translateText('treatment-page.messages.unsaved-edit-discard'),
      this._translatorService.translateText('treatment-page.buttons.cancel'),
      'soap-unsaved-changes-alert',
    );
    if (choice == null) {
      return false;
    }
    if (choice === 'save') {
      await adjust.saveSavableEncounter({ showToast: false });
    } else {
      await adjust.discardMetadataEdits();
    }
    this.metadataEditSession = false;
    this.metadataEditCloseResolved = true;
    this.suppressWorkingCurrentAfterMetadataEdit = true;
    if (this.patientId) {
      this.treatmentService.suppressResumeCurrentEncounter(this.patientId);
    }
    this.applyMenuPageDisabledState();
    return true;
  }

  private snapshotAdjustPlaylist(): void {
    const playlist = this.adjustmentComponent?.playlist;
    if (playlist?.length) {
      this.lastAdjustPlaylist = playlist.map((item) => ({ ...item }));
    }
  }

  private async persistAdjustChangesIfNeeded(): Promise<void> {
    if (this.metadataEditSession) {
      return;
    }
    if (this.suppressWorkingCurrentAfterMetadataEdit) {
      if (this.patientId) {
        this.treatmentService.suppressResumeCurrentEncounter(this.patientId);
        await this.treatmentService.clearCurrentEncounter(this.patientId);
      }
      this.metadataEditCloseResolved = false;
      return;
    }
    if (await this.clearCurrentAfterMetadataEditIfNeeded()) {
      return;
    }
    this.snapshotAdjustPlaylist();
    await this.syncAdjustEncounterToCacheIfNeeded();
    await this.linkPendingQueuedTreatmentToCurrentEncounter();
  }

  /** Edit is never the working CURRENT visit — drop draft/cache so Profile cannot show Continue. */
  private async clearCurrentAfterMetadataEditIfNeeded(): Promise<boolean> {
    if (!this.metadataEditCloseResolved) {
      return false;
    }
    if (this.patientId) {
      this.treatmentService.suppressResumeCurrentEncounter(this.patientId);
      await this.treatmentService.clearCurrentEncounter(this.patientId);
    }
    this.metadataEditCloseResolved = false;
    return true;
  }

  /** Persist the saved CURRENT encounter onto the open queue so Profile can treat them as one visit. */
  private async linkPendingQueuedTreatmentToCurrentEncounter(): Promise<void> {
    const queuedTreatment = this.pendingQueuedTreatment;
    if (!queuedTreatment?.id || !this.patientId) {
      return;
    }

    const current = await this.treatmentService.getCurrentEncounter(this.patientId, {
      allowWhenUninitialized: true,
    });
    const encounterId = current?.id ?? null;
    if (
      !encounterId ||
      !shouldLinkQueuedTreatmentEncounter({
        queuedTreatmentId: queuedTreatment.id,
        encounterId,
      })
    ) {
      return;
    }

    await this.linkQueuedTreatmentEncounter(queuedTreatment, encounterId);
  }

  /** Wait until Adjust/Review child exists and finishes SetupPage (or timeout). */
  private async waitForActiveTabSetupReady(): Promise<void> {
    const deadline = Date.now() + 20000;
    while (Date.now() < deadline) {
      if (this.pageView === 'adjust') {
        const adjust = this.adjustmentComponent;
        if (adjust?.isSetupComplete) {
          await adjust.whenSetupReady();
          return;
        }
      } else if (this.pageView === 'review') {
        const review = this.reviewComponent;
        if (review) {
          await review.whenSetupReady();
          return;
        }
      } else {
        return;
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 16));
      this.cdr.detectChanges();
    }
  }

  async LoadProfileComponent() {
    if (this.pageView === 'profile' && !(await this.canLeaveProfile())) {
      return;
    }
    if (!(await this.canLeaveAdjustMetadataEdit())) {
      return;
    }
    if (!this.beginTabNavigation()) {
      return;
    }
    try {
      await this.persistAdjustChangesIfNeeded();
      this.pageView = 'profile';
      this.showCurrentVisitBadge = false;
      this.navDisabledPrev = false;
      this.navDisabledNext = false;
      this.appHeaderComponent?.setCurrentPage(this.profilePage);

      this.actionButtons = [];
      this.navLine1 = '';
      this.navLine2 = '';

      this.actionButtons = [];

      if (this.patientId && !this.suppressWorkingCurrentAfterMetadataEdit) {
        const currentEncounter = await this.treatmentService.getCurrentEncounter(this.patientId);
        if (
          (!currentEncounter || !TreatmentService.shouldKeepCurrentEncounter(currentEncounter)) &&
          !this.treatmentService.wasOpenedInAdjust(this.patientId)
        ) {
          await this.treatmentService.resolveCurrentEncounterForPatient(this.patientId, {
            hasRecallPermission: this.hasTreatmentProfileRecall,
          });
        }
      }
    } finally {
      this.endTabNavigation();
    }
  }

  async LoadAdjustComponent(
    expandAssignSection?: boolean,
    recallEncounterId: string | null = null,
    options?: { skipEncounterResume?: boolean; skipQueuedPartsHydration?: boolean },
  ) {
    if (!this.canAccessAdjustView()) {
      return;
    }
    if (!this.supportsFullAdjust && recallEncounterId == null && !this.metadataEditSession) {
      return;
    }
    if (!(await this.canLeaveProfile())) {
      return;
    }
    if (!this.beginTabNavigation()) {
      return;
    }
    try {
      this.pageView = 'adjust';
      this.showCurrentVisitBadge = false;
      this.navDisabledPrev = false;
      this.navDisabledNext = false;
      this.appHeaderComponent?.setCurrentPage(
        this.metadataEditSession ? this.reviewPage : this.adjustPage,
      );

      const dateObj = new Date(Date.now());
      this.dateText = new Intl.DateTimeFormat('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }).format(dateObj);

      this.navLine1 = this.dateText;
      this.navLine2 = this.visitNumber;
      this.buildAdjustActionButtons();

      let effectiveExpandAssignSection = expandAssignSection;
      let effectiveRecallId = recallEncounterId;
      let skipQueuedPartsHydration = options?.skipQueuedPartsHydration ?? false;

      if (options?.skipEncounterResume && this.patientId) {
        const openQueueId = this.pendingQueuedTreatment?.id;
        const keepLocalDraft =
          !!openQueueId &&
          (await this.treatmentService.shouldResumeUnlinkedQueuedDraft(
            this.patientId,
            openQueueId,
          ));
        if (!keepLocalDraft) {
          await this.treatmentService.setCurrentEncounter(null, this.patientId);
        }
        if (effectiveExpandAssignSection === undefined) {
          effectiveExpandAssignSection = true;
        }
        effectiveRecallId = null;
      } else if (effectiveRecallId == null && this.patientId) {
        const currentEncounter = await this.treatmentService.getCurrentEncounter(this.patientId, {
          allowWhenUninitialized: true,
        });
        const shouldResume = this.treatmentService.shouldResumeCurrentEncounterOnAdjust(
          currentEncounter,
          this.patientId,
        );

        if (!shouldResume) {
          await this.treatmentService.setCurrentEncounter(null, this.patientId);
          if (effectiveExpandAssignSection === undefined) {
            // Adjust tab: start collapsed. New Treatment passes true explicitly.
            effectiveExpandAssignSection = false;
          }
        } else {
          // Resume the in-progress encounter explicitly so SetupPage does not race with a null id.
          effectiveRecallId = currentEncounter?.id ?? null;
          skipQueuedPartsHydration = skipQueuedPartsHydration || this.queuedAdjustOpenApplied;
          if (effectiveExpandAssignSection === undefined) {
            effectiveExpandAssignSection = false;
          }
        }
      } else if (effectiveRecallId != null) {
        skipQueuedPartsHydration =
          skipQueuedPartsHydration || (this.queuedAdjustOpenApplied && !options?.skipEncounterResume);
        if (effectiveExpandAssignSection === undefined) {
          effectiveExpandAssignSection = false;
        }
      } else if (effectiveExpandAssignSection === undefined) {
        effectiveExpandAssignSection = false;
      }

      if (this.metadataEditSession) {
        effectiveExpandAssignSection = true;
      }

      this.deferredApplyService.queue<TreatmentAdjustUiOptions>('treatment-adjust', {
        expandAssignSection: effectiveExpandAssignSection,
        encounterId: effectiveRecallId,
        skipQueuedPartsHydration,
      });

      this.treatmentService.initialized = true;
      this.cdr.detectChanges();
      await this.waitForActiveTabSetupReady();
    } finally {
      this.endTabNavigation();
    }
  }

  async LoadReviewComponent() {
    if (!this.hasTreatmentProfileReview) {
      return;
    }
    if (!(await this.canLeaveProfile())) {
      return;
    }
    if (!(await this.canLeaveAdjustMetadataEdit())) {
      return;
    }
    if (!this.beginTabNavigation()) {
      return;
    }
    try {
      await this.persistAdjustChangesIfNeeded();

      // Only force working CURRENT when leaving Adjust → Review (including empty drafts).
      // Profile → Review must keep the encounter selected via setCurrentReviewEncounter.
      const comingFromAdjust = this.pageView === 'adjust';
      if (this.patientId && comingFromAdjust && !this.suppressWorkingCurrentAfterMetadataEdit) {
        const current = await this.treatmentService.getCurrentEncounter(this.patientId);
        if (current && this.treatmentService.wasOpenedInAdjust(this.patientId)) {
          // Ensure empty drafts exist in the encounter list so Review can resolve them.
          this.treatmentService.updateEncounter(current, !current.id);
          await this.treatmentService.setCurrentReviewEncounter(current.id ?? null);
        }
      }

      this.pageView = 'review';
      this.appHeaderComponent?.setCurrentPage(this.reviewPage);

      this.navLine1 = this.dateText;
      this.navLine2 = this.paginationText;
      this.buildReviewActionButtons();
      this.cdr.detectChanges();
      await this.waitForActiveTabSetupReady();
    } finally {
      this.endTabNavigation();
    }
  }

  get navigationEnabled(): boolean {
    return this.pageView == 'review';
  }

  async LoadResultComponent() {
    if (!this.hasTreatmentProfileResultsView) {
      return;
    }
    if (!(await this.canLeaveProfile())) {
      return;
    }
    if (!(await this.canLeaveAdjustMetadataEdit())) {
      return;
    }
    if (!this.beginTabNavigation()) {
      return;
    }
    try {
      await this.persistAdjustChangesIfNeeded();
      this.showCurrentVisitBadge = false;
      this.navDisabledPrev = false;
      this.navDisabledNext = false;
      this.appHeaderComponent?.setCurrentPage(this.resultPage);
      this.navLine1 = '';
      this.navLine2 = '';
      this.pageView = 'result';
      this.actionButtons = [];
    } finally {
      this.endTabNavigation();
    }
  }

  async onTreatmentCompleted(e: boolean) {
    if (e) {
      this.closeProfile();
    }
  }

  get isProfileSelected() {
    return this.pageView == 'profile';
  }
  get isAdjustSelected() {
    return this.pageView == 'adjust';
  }
  get isReviewSelected() {
    return this.pageView == 'review';
  }
  get isResultSelected() {
    return this.pageView == 'result';
  }
  async OpenEncounter(event: EncounterOpenEvent | EncounterOpenMode = 'review') {
    const { mode, encounterId, queuedTreatmentId } =
      typeof event === 'string'
        ? {
            mode: event,
            encounterId: null as string | null,
            queuedTreatmentId: null as string | null,
          }
        : event;
    if (mode === 'openQueued') {
      await this.openQueuedVisit(encounterId, queuedTreatmentId);
      return;
    }
    const opensAdjust =
      mode === 'recall' ||
      mode === 'edit' ||
      mode === 'newTreatment' ||
      mode === 'continueCurrent';

    if (opensAdjust) {
      if (!this.canAccessAdjustView()) {
        return;
      }
      if (!this.supportsFullAdjust && mode !== 'recall' && mode !== 'edit') {
        return;
      }
    } else if (!this.hasTreatmentProfileReview) {
      return;
    }
    if (this.pageView === 'profile' && !(await this.canLeaveProfile())) {
      return;
    }

    if (opensAdjust) {
      if (mode === 'continueCurrent') {
        this.metadataEditSession = false;
        this.suppressWorkingCurrentAfterMetadataEdit = false;
        if (this.patientId) {
          this.treatmentService.allowResumeCurrentEncounter(this.patientId);
        }
        this.clearQueuedPartsSnapshotForAdjustResume();
        const recallId = await this.resolveContinueCurrentRecallId(encounterId);
        await this.LoadAdjustComponent(false, recallId, { skipQueuedPartsHydration: true });
        return;
      }
      if (mode === 'edit') {
        if (!this.hasTreatmentProfileEdit) {
          return;
        }
        this.metadataEditSession = true;
        this.metadataEditCloseResolved = false;
        this.suppressWorkingCurrentAfterMetadataEdit = false;
        await this.LoadAdjustComponent(true, encounterId ?? null);
        return;
      }
      this.metadataEditSession = false;
      this.suppressWorkingCurrentAfterMetadataEdit = false;
      if (this.patientId) {
        this.treatmentService.allowResumeCurrentEncounter(this.patientId);
      }
      const recallId = mode === 'recall' ? (encounterId ?? null) : null;
      await this.LoadAdjustComponent(mode === 'newTreatment', recallId);
    } else {
      // Profile already called setCurrentReviewEncounter; re-apply object payloads so the
      // selected treatment survives LoadReviewComponent when not coming from Adjust.
      if (typeof event !== 'string') {
        await this.treatmentService.setCurrentReviewEncounter(encounterId);
      }
      this.appHeaderComponent?.setCurrentPage(this.reviewPage);
      await this.LoadReviewComponent();
    }
  }

  private async openQueuedVisit(
    encounterId?: string | null,
    queuedTreatmentId?: string | null,
  ): Promise<void> {
    if (
      (!encounterId && !queuedTreatmentId) ||
      !this.canAccessAdjustView() ||
      !this.supportsFullAdjust
    ) {
      return;
    }

    const queuedTreatment = await startQueuedSession({
      loadQueue: () =>
        queuedTreatmentId
          ? firstValueFrom(
              this.queueService.patientTreatmentQueueIdGet({ id: queuedTreatmentId }),
            )
          : firstValueFrom(
              this.queueService.patientTreatmentQueueEncounterIdByEncounterGet({
                encounterId: encounterId!,
              }),
            ),
      markInProgress: (queued) =>
        firstValueFrom(
          this.queueService.patientTreatmentQueueIdUpdateStatusStatusPost({
            id: queued.id!,
            status: TreatmentQueueStatusType.InProgress,
          }),
        ),
    });
    if (!queuedTreatment) {
      return;
    }

    this.pendingQueuedTreatment = queuedTreatment;
    this.queuedTreatmentParts = queuedTreatment.parts ?? [];
    this.queuedTreatmentNotes = queuedTreatment.notes ?? null;
    this.queuedTreatmentRegionNotes = queuedTreatment.regionNotes ?? null;
    this.queuedTreatmentTreatmentNotes = queuedTreatment.treatmentNotes ?? null;
    this.skipNextPatientSelectedProfileLoad = true;
    await this.openQueuedAdjustIfPending();
  }

  async setRecallEnabled(params: boolean) {
    this.recallEnabled = params;
    this.buildReviewActionButtons();
  }

  onSetCompleteTreatmentEnabled(params: boolean) {
    this.enableCompleteEncounter = params;
  }

  async onRecallClicked() {
    if (!this.hasTreatmentProfileRecall) {
      return;
    }
    const encounterId = this.reviewComponent?.encounterDetailModel?.id ?? null;
    await this.OpenEncounter({ mode: 'recall', encounterId });
  }

  async duplicateTreatmentReady() {
    if (!this.hasTreatmentProfileDuplicate) {
      return;
    }
    await this.LoadAdjustComponent(true);
  }

  async onProfileUpdated(_patientModel: PatientModel) {
    this.headerTitle = getPatientFullName(_patientModel);
    this.patientModel = _patientModel;
  }

  async adjustNavPrevious() {
    // Left arrow: step toward older visits (list is newest-first).
    if (this.adjustmentComponent) {
      await this.adjustmentComponent.nextEncounter();
    }
    if (this.reviewComponent) {
      await this.reviewComponent.nextEncounter();
    }
  }

  async adjustNavNext() {
    // Right arrow: step toward newer visits.
    if (this.adjustmentComponent) {
      await this.adjustmentComponent.previousEncounter();
    }
    if (this.reviewComponent) {
      await this.reviewComponent.previousEncounter();
    }
  }

  async createProtocol() {
    if (this.adjustmentComponent) {
      await this.adjustmentComponent.createProtocol();
    }
    if (this.reviewComponent) {
      await this.reviewComponent.createProtocol();
    }
  }

  async completeTreatment() {
    if (this.metadataEditSession) {
      return;
    }
    if (this.adjustmentComponent) {
      await this.adjustmentComponent.completeTreatment(false);
    }
    if (this.reviewComponent) {
      await this.reviewComponent.completeTreatment(false);
    }
  }

  async recallTreatment() {
    if (!this.hasTreatmentProfileRecall) {
      return;
    }
    if (this.adjustmentComponent) {
      await this.adjustmentComponent.recall();
    }
    if (this.reviewComponent) {
      await this.reviewComponent.recall();
    }
  }

  async closeProfile(
    forceLeave: boolean = false,
    options?: {
      skipIdleReset?: boolean;
      skipSessionClear?: boolean;
      skipNavigation?: boolean;
    },
  ) {
    if (!forceLeave && !(await this.canLeaveProfile())) {
      return;
    }
    if (!forceLeave && !(await this.canLeaveAdjustMetadataEdit())) {
      return;
    }

    this.snapshotAdjustPlaylist();
    const closePlaylist = this.adjustmentComponent?.playlist ?? this.lastAdjustPlaylist;
    const savedQueuedTreatmentId = this.pendingQueuedTreatment?.id ?? null;
    let queueIncompleteVisit = false;
    let userChoseClose = false;
    if (!forceLeave && hasUntreatedAssignedParts(closePlaylist)) {
      const message = this.incompleteVisitPromptMessage(!!savedQueuedTreatmentId);
      const choice = await this._promptService.queueOrCloseVisitChoice(
        message,
        this._translatorService.translateText('patients.profile.incompleteVisit.queue'),
        this._translatorService.translateText('patients.profile.incompleteVisit.close'),
      );
      queueIncompleteVisit = choice === 'queue';
      userChoseClose = choice === 'close';
    }

    const queuedTreatment = this.pendingQueuedTreatment;
    const queuedTreatmentId = queuedTreatment?.id ?? null;
    const queuedPlaylist = closePlaylist;
    let encounterTreatments = this.adjustmentComponent?.encounterDetailModel?.treatments ?? null;
    let savedEncounterId: string | null = null;

    if (this.adjustmentComponent) {
      if (await this.clearCurrentAfterMetadataEditIfNeeded()) {
        // Metadata edit already saved/discarded; do not promote that visit to CURRENT.
      } else {
        // Attempt save (or offline queue) then always continue; keep draft if durability fails.
        await this.adjustmentComponent.completeAndClearCurrentEncounter({
          showToast: false,
          onlyIfDirty: true,
        });
      }
      encounterTreatments =
        this.adjustmentComponent.encounterDetailModel?.treatments ?? encounterTreatments;
      savedEncounterId = this.adjustmentComponent.encounterDetailModel?.id ?? null;
    } else if (this.patientId) {
      const currentEncounter = await this.treatmentService.getCurrentEncounter(this.patientId, {
        allowWhenUninitialized: true,
      });
      const hadSavableTreatments =
        !!currentEncounter && TreatmentService.encounterHasSavableTreatments(currentEncounter);
      const shouldPersistDirty =
        hadSavableTreatments &&
        (!currentEncounter?.id ||
          this.treatmentService.encounterHasNewTreatments(currentEncounter, this.patientId));
      if (shouldPersistDirty) {
        const facility =
          this.selectedFacility ?? (await this._practitionerService.getSelectedFacility());
        if (facility?.id) {
          const durable = await this.treatmentService.completeCachedEncounter(
            this.patientId,
            facility.id,
          );
          const persisted = await this.treatmentService.getCurrentEncounter(this.patientId, {
            allowWhenUninitialized: true,
          });
          savedEncounterId = persisted?.id ?? currentEncounter?.id ?? null;
          if (durable) {
            await this.treatmentService.clearCurrentEncounter(this.patientId);
          }
        } else {
          console.warn(
            'closeProfile: cannot persist dirty encounter — no selected facility resolved',
          );
        }
      } else {
        savedEncounterId = currentEncounter?.id ?? null;
      }
      encounterTreatments = encounterTreatments ?? currentEncounter?.treatments ?? null;
    }

    // Linking only applies to an in-progress queued treatment; the queue upsert below still
    // needs the saved encounter so the API can match an existing queue instead of creating one.
    const encounterIdToLink = shouldLinkQueuedTreatmentEncounter({
      queuedTreatmentId,
      encounterId: savedEncounterId,
    })
      ? savedEncounterId
      : null;

    if (queuedTreatment && encounterIdToLink) {
      await this.linkQueuedTreatmentEncounter(queuedTreatment, encounterIdToLink);
    }

    if (queueIncompleteVisit) {
      await this.upsertIncompleteVisitQueue(
        closePlaylist ?? [],
        incompleteVisitQueueTarget({ queuedTreatmentId, savedEncounterId }),
        encounterTreatments,
      );
    } else if (
      queuedTreatmentId &&
      shouldCompleteSavedQueuedTreatmentOnClose({
        queuedTreatmentId,
        userChoseClose,
        allAssignedPartsTreated: shouldCompleteQueuedTreatment({
          playlist: queuedPlaylist,
          queuedParts: queuedTreatment?.parts,
          treatedPartKeys: treatedEncounterPartKeys(encounterTreatments),
        }),
      })
    ) {
      await this.completeQueuedTreatmentStatus(queuedTreatmentId);
    }

    this.treatmentService.initialized = false;
    this.clearQueuedTreatmentOpenState();
    // Stay on the current tab (Adjust/Review/Result). Switching to Profile here
    // flashes that tab before /welcome and unmounts Adjust before cleanup.
    this.patientSessionClosed = true;
    if (!options?.skipIdleReset) {
      IdleTimeoutService.reset();
    }

    if (options?.skipNavigation) {
      // Idle logout path: persist only; SessionService performs logout redirect.
      return;
    }

    if (options?.skipSessionClear) {
      // Leave cache for a caller that still needs it, then navigate away.
      await this._router.navigateByUrl('/welcome', { replaceUrl: true });
      return;
    }

    if (this.patientId) {
      this.treatmentService
        .clearPatientSession(this.patientId)
        .then(async () => {
          this.adjustmentComponent?.clearPendingRecallState();
          this.deferredApplyService.clear('treatment-adjust');
        })
        .finally(async () => {
          await this._router.navigateByUrl('/welcome', { replaceUrl: true });
        });
    } else {
      await this._router.navigateByUrl('/welcome', { replaceUrl: true });
    }
  }

  private incompleteVisitPromptMessage(
    hasSavedQueuedTreatment: boolean,
  ): string | IonicSafeString {
    const message = this._translatorService.translateText('patients.profile.incompleteVisit.message');
    if (!hasSavedQueuedTreatment) {
      return message;
    }

    const closeCompletes = this._translatorService.translateText(
      'patients.profile.incompleteVisit.closeCompletesQueued',
    );
    return new IonicSafeString(`${message}<i class="close-completes-queued">${closeCompletes}</i>`);
  }

  private async upsertIncompleteVisitQueue(
    playlist: ITreatmentPlayListItem[],
    target: { isCreateMode: boolean; queueId: string | null; encounterId: string | null },
    encounterTreatments?: Array<{
      success?: boolean | null;
      dose?: number | null;
      mobilityPrePercentage?: number | null;
      mobilityPostPercentage?: number | null;
    }> | null,
  ): Promise<void> {
    const patientId = this.patientId;
    const facility =
      this.selectedFacility ?? (await this._practitionerService.getSelectedFacility());
    const clinicGroupId = facility?.clinicGroupId;
    if (!patientId || !clinicGroupId) {
      return;
    }

    const sessionHadTreatments =
      !target.isCreateMode &&
      queuedSessionHadTreatments({
        adjustmentCountAtOpen: this.queuedSessionAdjustmentCountAtOpen,
        adjustmentCountNow: countQueuedSessionAdjustments(
          encounterTreatments
            ?? this.adjustmentComponent?.encounterDetailModel?.treatments
            ?? playlist.flatMap((row) => row.treatments ?? []),
        ),
      });
    const leftoverFindingKeys = sessionHadTreatments
      ? untreatedQueuedFindingKeys(playlist)
      : null;
    const notes = sessionHadTreatments
      ? this.queuedTreatmentNotes
      : (this.adjustmentComponent?.encounterDetailModel?.notes
        ?? (patientId ? this.treatmentService.getPendingSoapNotes(patientId) : null));
    const regionNotes = sessionHadTreatments
      ? this.queuedTreatmentRegionNotes
      : resolveRegionNotesForDuplicate({
        regionNotes: this.adjustmentComponent?.encounterDetailModel?.regionNotes,
        pendingRegionPartDataByNoteRegion: patientId
          ? this.treatmentService.getPendingRegionPartDataByNoteRegion(patientId)
          : null,
        objectiveNotes: notes?.objectiveNotes ?? null,
      });

    try {
      await firstValueFrom(
        this.queueService.patientTreatmentQueuePatientTreatmentQueuePost({
          body: buildPatientTreatmentQueuePostBody({
            isCreateMode: target.isCreateMode,
            queueId: target.queueId,
            clinicGroupId,
            patientId,
            loadedEncounterId: target.encounterId,
            loadedStatus: TreatmentQueueStatusType.Queued,
            playlist,
            notes,
            regionNotes,
            treatmentNoteKeys: leftoverFindingKeys,
            statusOverride: TreatmentQueueStatusType.Queued,
          }),
        }),
      );
    } catch (error) {
      await this._promptService.showNetworkConnectionError(error, 'patient-treatment-queue-save');
    }
  }

  async duplicateTreatment() {
    if (!this.hasTreatmentProfileDuplicate) {
      return;
    }
    if (this.adjustmentComponent) {
      await this.adjustmentComponent.duplicateTreatment();
    }
    if (this.reviewComponent) {
      await this.reviewComponent.duplicateTreatment();
    }
  }
}

export interface ITreatmentRowItem extends ITreatment {
  mobilityRangePercent?: number | null;
  bodyPartId?: number | null;
}

export interface ITreatmentPlayListItem {
  partId: number;
  maskId?: number;
  bodyPart?: BodyPart;
  vector?: string | null;
  partFindings?: string | null;
  findings?: PartFindingModel[] | null;
  treatmentNoteDescription?: string | null;
  side?: string | null;
  treatments?: ITreatmentRowItem[];
}

export interface DisplayTreatmentPart {
  partItem: IPartItem;
  addendumData?: DisplayTreatmentAddendumData;
  treatments: ITreatmentRowItem[];
}

export interface DisplayTreatmentAddendumData {
  partFindings?: string | null;
  findings?: PartFindingModel[] | null;
  treatmentNoteDescription?: string | null;
  treatmentVector?: string | null;
  treatmentSide?: string | null;
}

export interface TimeoutRef {
  value: any;
}

export interface EncounterHistoryItem extends PatientEncounterModel {
  recallEnabled: boolean;
  practitionerDisplayName?: string | null;
  practitionerId?: string | null;
  queuedTreatmentId?: string | null;
}

export enum TreatmentModeType {
  Append,
  Advance,
}
